/**
 * Compute a deterministic geometry hash from shape-defining inputs.
 * Uses djb2 hashing on a string built from vertices, depth, and rotation.
 * Floats are rounded to 4 decimal places to absorb floating-point noise.
 *
 * NOTE: textureKey is intentionally excluded. Material/texture changes are
 * handled by dedicated cross-fade methods (changeSurroundTexture, etc.)
 * and slat transitions by rebuildSurrounds(). Including textureKey would
 * cause the diff to treat texture-only changes as geometry replacements.
 */
export const computeGeometryHash = (
	vertices: Array<{ x: number; y: number }>,
	depth: number,
	rotation?: { x: number; y: number; z: number }
): string => {
	const parts: string[] = [];

	for (const v of vertices) {
		parts.push(`${v.x.toFixed(4)},${v.y.toFixed(4)}`);
	}

	parts.push(`d:${depth.toFixed(4)}`);

	if (rotation) {
		parts.push(`r:${rotation.x.toFixed(4)},${rotation.y.toFixed(4)},${rotation.z.toFixed(4)}`);
	}

	// djb2 hash
	const str = parts.join('|');
	let hash = 5381;
	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
	}
	return hash.toString(36);
};
